import React, { useEffect, useState, useCallback } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable, ScrollView, ActivityIndicator, Modal } from '../../lib/react-native-compat';
import { selectedJobAtom, selectedJobDataAtom, userNameAtom, jobPhaseCodesAtom, jobOpenCommitmentsAtom, jobCostDetailAtom } from '../../store/company';
import { loadFieldReports, loadFieldReport, saveFieldReport, deleteFieldReport } from '../../db/company';
import { showToast } from '../../helpers/toast';
import {
    fieldReportAtom, fieldReportStepAtom, fieldReportSavingAtom, fieldReportDirtyAtom,
    fieldReportViewAtom, newReport, buildSavePayload, hydrateFromRead,
} from './state';
import { queueSave, dequeueSave, queuedCount, flushQueue } from './offline';
import { wizardSteps, simpleWizardSteps, jsonSectionKeys, jobWorkTypes } from './sectionDefs';
import { isPipelineJob } from './qtyModules';
import WorkPhaseChips, { useJobPhases } from './WorkPhaseChips';
import SetupStep from './SetupStep';
import SectionRowsStep from './SectionRowsStep';
import QuantityCards from './QuantityCards';
import WizardNav from './WizardNav';
import SheetView from './SheetView';
import SimpleReview from './SimpleReview';
import TimeCard from './TimeCard';
import SubCards from './SubCards';
import EquipmentMatrix from './EquipmentMatrix';
import JobSwitcher from './JobSwitcher';
import ReportCalendar from './ReportCalendar';
import JobHeader from './JobHeader';

// Daily Field Report tab. List of reports for the selected job → multi-step
// wizard (setup → sections → review/submit). The review step renders the full
// SheetView so the last look before submit is the printable sheet. The whole
// report saves in one call on step changes / Save Draft / Submit; photos
// upload separately.
//
// simple: the Quick Report variant (/daily-report2) — two tabs only. Progress
// carries the day's schedule (phase Gantt + chip picker) over the per-phase
// card (note, people & hours, today's quantity); Equipment carries the
// equipment matrix and the subs on site, then the filing block that used to be
// the Review tab (conditions, superintendent, one-question safety, issues,
// day recap, photos, notes) and the Save Draft / Submit buttons. No Setup tab,
// no Matl/Quality tab and no Guided/Sheet toggle. Same report storage, so
// reports open in either UI — the sections the Quick Report drops still hold
// whatever the full report put there.

const DailyFieldReport = ({ simple }) => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [jobData] = useAtom(selectedJobDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailAtom);
    const [userName] = useAtom(userNameAtom);
    const [phaseCodes] = useAtom(jobPhaseCodesAtom);
    const [openCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [report, setReport] = useAtom(fieldReportAtom);
    const [step, setStep] = useAtom(fieldReportStepAtom);
    const [saving, setSaving] = useAtom(fieldReportSavingAtom);
    const [dirty, setDirty] = useAtom(fieldReportDirtyAtom);
    const [viewMode, setViewMode] = useAtom(fieldReportViewAtom);

    const { jobPhases } = useJobPhases();

    const [reports, setReports] = useState(null);
    const [loadingList, setLoadingList] = useState(false);
    const [openingId, setOpeningId] = useState(null);
    const [showReportPicker, setShowReportPicker] = useState(false);
    // Drafts collapse into one section on the list so submitted reports read
    // first; open it to see the individual drafts.
    const [draftsOpen, setDraftsOpen] = useState(false);

    // Offline mode: saves that can't reach the API queue on-device and sync
    // when the connection returns.
    const [isOnline, setIsOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
    const [pendingSync, setPendingSync] = useState(queuedCount());
    useEffect(() => {
        const flush = async () => {
            setIsOnline(true);
            if (!queuedCount()) return;
            const { sent, failed } = await flushQueue(saveFieldReport);
            setPendingSync(queuedCount());
            if (sent) showToast(`Back online — synced ${sent} queued report save${sent === 1 ? '' : 's'}.`, 'success');
            if (failed) showToast('Some queued saves still failed — they will retry.', 'error');
        };
        const onOffline = () => setIsOnline(false);
        window.addEventListener('online', flush);
        window.addEventListener('offline', onOffline);
        if (navigator.onLine && queuedCount()) flush();
        return () => {
            window.removeEventListener('online', flush);
            window.removeEventListener('offline', onOffline);
        };
    }, []);

    const readOnly = report?.status === 'Submitted';

    // Leaving the page (refresh, close, mobile tab switch) with unsaved edits:
    // snapshot the whole report into the offline queue synchronously — a
    // network save can't be trusted to finish during unload. It flushes on the
    // next open like any offline save (latest-wins per reportId), and a
    // successful doSave dequeues it so a stale snapshot can never replay over
    // newer server data. Refs keep the one-time listeners reading live state.
    const leaveRef = React.useRef({});
    leaveRef.current = { report, dirty, readOnly, userName };
    useEffect(() => {
        const snapshot = () => {
            const { report, dirty, readOnly, userName } = leaveRef.current;
            if (!report || !dirty || readOnly) return;
            try { queueSave(buildSavePayload(report, userName)); } catch (_) { /* storage full */ }
        };
        const onVis = () => { if (document.visibilityState === 'hidden') snapshot(); };
        window.addEventListener('pagehide', snapshot);
        document.addEventListener('visibilitychange', onVis);
        return () => {
            window.removeEventListener('pagehide', snapshot);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, []);

    const refreshList = useCallback(async () => {
        if (!selectedJob) return;
        setLoadingList(true);
        const res = await loadFieldReports({ buId: String(selectedJob) });
        setLoadingList(false);
        setReports(res?.reports || []);
    }, [selectedJob]);

    useEffect(() => {
        if (!report) refreshList();
    }, [refreshList, report]);

    // Report opened for a different job than the one now selected → back to list.
    useEffect(() => {
        if (report && selectedJob && report.buId !== String(selectedJob)) {
            setReport(null);
        }
    }, [selectedJob]);

    const doSave = async (statusOverride) => {
        if (!report || readOnly) return null;
        setSaving(true);
        try {
            const payload = buildSavePayload(report, userName, statusOverride);
            const res = await saveFieldReport(payload);
            if (!res || res.error) {
                // Network down (postRequest resolves undefined) → queue the
                // payload on-device; it syncs when the connection returns.
                if (!res) {
                    queueSave(payload);
                    setPendingSync(queuedCount());
                    setDirty(false);
                    showToast(
                        statusOverride === 'Submitted'
                            ? 'Offline — submit queued on this device; it will send when you reconnect.'
                            : 'Offline — saved on this device; it will sync when you reconnect.',
                        'success',
                    );
                    return null;
                }
                showToast('Save failed — your entries are still on this device. Try again.', 'error');
                return null;
            }
            setReport((r) => r ? {
                ...r,
                reportNumber: res.ReportNumber ?? r.reportNumber,
                status: res.Status || r.status,
            } : r);
            setDirty(false);
            // A page-leave snapshot (below) may sit in the queue from earlier;
            // the server now has something at least as new, so drop it rather
            // than let a later flush overwrite fresher edits.
            dequeueSave(payload.report.reportId);
            setPendingSync(queuedCount());
            return res;
        } finally {
            setSaving(false);
        }
    };

    const onStep = (i) => {
        if (!readOnly && dirty && !saving) doSave();
        setStep(i);
        try { window.scrollTo({ top: 0 }); } catch (_) {}
    };

    const switchView = (mode) => {
        if (mode === viewMode) return;
        if (!readOnly && dirty && !saving) doSave();
        setViewMode(mode);
        try { localStorage.setItem('fieldReportView', mode); } catch (_) {}
    };

    const onSaveDraft = async () => {
        const res = await doSave('Draft');
        if (res) showToast('Draft saved.', 'success');
    };

    const onSubmit = async () => {
        const res = await doSave('Submitted');
        if (res) {
            showToast(`Report #${res.ReportNumber} submitted.`, 'success');
            setReport(null);
            setStep(0);
        }
    };

    const openNew = () => {
        setReport(newReport({ buId: String(selectedJob), preparedBy: userName }));
        setStep(0);
        setDirty(false);
    };

    const openExisting = async (row) => {
        setOpeningId(row.ReportID);
        const res = await loadFieldReport({ reportId: row.ReportID });
        setOpeningId(null);
        if (!res || res.error || !res.report) {
            showToast('Could not open the report — try again.', 'error');
            return;
        }
        setReport(hydrateFromRead(res));
        setStep(0);
        setDirty(false);
    };

    const removeDraft = async (row) => {
        if (!window.confirm(`Delete draft report #${row.ReportNumber}? This cannot be undone.`)) return;
        const res = await deleteFieldReport({ reportId: row.ReportID, updatedBy: userName });
        if (!res || res.error) {
            showToast('Delete failed — only drafts can be deleted.', 'error');
            return;
        }
        refreshList();
    };

    const backToList = () => {
        if (!readOnly && dirty && !saving) doSave();
        setReport(null);
        setStep(0);
    };

    // Amber strip whenever we're offline (or still holding queued saves).
    const offlineBanner = (!isOnline || pendingSync > 0) ? (
        <View style={{ backgroundColor: '#FEF3C7', borderBottom: '1px solid #FDE68A', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12 }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: '#92400E', textAlign: 'center' }}>
                {!isOnline
                    ? 'Offline — entries save to this device and sync automatically when you reconnect. Photo uploads need a connection.'
                    : `${pendingSync} save${pendingSync === 1 ? '' : 's'} waiting to sync…`}
            </Text>
        </View>
    ) : null;

    if (!selectedJob) {
        return (
            <View style={{ padding: 24, alignItems: 'center' }}>
                <Text style={{ fontSize: 14, color: '#64748B', marginBottom: 12 }}>Select a job to file or view Daily Field Reports.</Text>
                <JobSwitcher />
            </View>
        );
    }

    // ── List view ────────────────────────────────────────────────────────────
    if (!report) {
        const todaysDraft = (reports || []).find(
            (r) => r.Status === 'Draft' && String(r.ReportDate).slice(0, 10) === new Date().toISOString().slice(0, 10),
        );
        const draftRows = (reports || []).filter((r) => r.Status === 'Draft');
        const otherRows = (reports || []).filter((r) => r.Status !== 'Draft');

        const reportCard = (r) => (
            <Pressable
                key={r.ReportID}
                onPress={() => openExisting(r)}
                style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 14, marginBottom: 10, cursor: 'pointer' }}
            >
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>
                        {`#${r.ReportNumber} — ${String(r.ReportDate).slice(0, 10)}`}
                    </Text>
                    <View style={{ paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: 10, borderRadius: 999, backgroundColor: r.Status === 'Submitted' ? '#ECFDF5' : '#E0F2FE' }}>
                        <Text style={{ fontSize: 12, fontWeight: '700', color: r.Status === 'Submitted' ? '#065F46' : '#075985' }}>
                            {openingId === r.ReportID ? 'Opening…' : r.Status}
                        </Text>
                    </View>
                </View>
                <Text style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                    {[
                        r.PreparedBy ? `By ${r.PreparedBy}` : null,
                        `${(r.LaborRowCount || 0) + (r.QuantityRowCount || 0) + (r.SectionRowCount || 0)} entries`,
                        `${r.PhotoCount || 0} photos`,
                    ].filter(Boolean).join(' · ')}
                </Text>
                {r.Status === 'Draft' && (
                    <Pressable onPress={(e) => { e?.stopPropagation?.(); removeDraft(r); }} style={{ marginTop: 8, alignSelf: 'flex-start', cursor: 'pointer' }}>
                        <Text style={{ fontSize: 12, color: '#DC2626', fontWeight: '600' }}>Delete draft</Text>
                    </Pressable>
                )}
            </Pressable>
        );

        return (
            <ScrollView style={{ flex: 1 }}>
                {offlineBanner}
                <View style={{ width: '100%', maxWidth: 760, marginLeft: 'auto', marginRight: 'auto', padding: 16 }}>
                    {/* Hero header — job, schedule dates, and the job switcher
                        (the standalone route has no top menu). */}
                    <JobHeader />

                    {/* Resume / New sit side by side; wraps to stacked full-width
                        buttons on narrow phones. */}
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 10, rowGap: 10, marginBottom: 18 }}>
                        {todaysDraft && (
                            <Pressable
                                onPress={() => openExisting(todaysDraft)}
                                style={{ flex: 1, minWidth: 220, minHeight: 56, borderRadius: 12, backgroundColor: '#1D4ED8', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 15, fontWeight: '700', color: '#fff', textAlign: 'center' }}>
                                    {openingId === todaysDraft.ReportID ? 'Opening…' : `Resume today's draft (#${todaysDraft.ReportNumber})`}
                                </Text>
                            </Pressable>
                        )}

                        <Pressable
                            onPress={openNew}
                            style={{ flex: 1, minWidth: 220, minHeight: 56, borderRadius: 12, backgroundColor: todaysDraft ? '#EFF6FF' : '#1D4ED8', border: todaysDraft ? '1px solid #BFDBFE' : 'none', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        >
                            <Text style={{ fontSize: 15, fontWeight: '700', color: todaysDraft ? '#1D4ED8' : '#fff' }}>+ New Report</Text>
                        </Pressable>
                    </View>

                    {reports && <ReportCalendar reports={reports} onPick={openExisting} />}

                    {loadingList && !reports && (
                        <View style={{ alignItems: 'center', padding: 20 }}>
                            <ActivityIndicator />
                        </View>
                    )}

                    {draftRows.length > 0 && (
                        <View style={{ marginBottom: 10 }}>
                            <Pressable
                                onPress={() => setDraftsOpen((v) => !v)}
                                style={{
                                    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                                    borderRadius: 12, backgroundColor: '#F0F9FF', border: '1px solid #BAE6FD',
                                    padding: 14, cursor: 'pointer', marginBottom: draftsOpen ? 10 : 0,
                                }}
                            >
                                <Text style={{ fontSize: 14, fontWeight: '700', color: '#075985' }}>
                                    {`Drafts (${draftRows.length})`}
                                </Text>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: '#0284C7' }}>
                                    {draftsOpen ? 'Hide ▲' : 'Show ▼'}
                                </Text>
                            </Pressable>
                            {draftsOpen && draftRows.map(reportCard)}
                        </View>
                    )}

                    {otherRows.map(reportCard)}

                    {reports && reports.length === 0 && !loadingList && (
                        <View style={{ borderRadius: 12, border: '1px dashed #CBD5E1', padding: 24, alignItems: 'center', backgroundColor: '#F8FAFC' }}>
                            <Text style={{ fontSize: 14, color: '#64748B' }}>No reports for this job yet.</Text>
                        </View>
                    )}
                </View>
            </ScrollView>
        );
    }

    // ── Wizard view ──────────────────────────────────────────────────────────
    // The step atom is shared between the full and Quick wizards, so clamp the
    // index in case a report was last open in the longer one.
    const steps = simple ? simpleWizardSteps : wizardSteps;
    const stepDef = steps[Math.min(step, steps.length - 1)];
    // Quick Report has no Sheet view — always guided.
    const effectiveView = simple ? 'wizard' : viewMode;
    // Cost-code pickers list only this job's phases (cost detail + Burn Tool
    // schedule). Full-dictionary fallback covers the moment before job data
    // loads; free text is still allowed everywhere.
    const jobCodes = Object.keys(jobPhases);
    const codes = jobCodes.length ? jobCodes.sort((a, b) => a.localeCompare(b)) : Object.keys(phaseCodes || {});
    const descOfCode = (c) => jobPhases[c] || phaseCodes?.[c] || '';
    // Vendors / PO numbers from the job's open PO lines feed the Material
    // Deliveries suggestions (cards + sheet view). Free text is still allowed.
    const poVendors = [...new Set((openCommitments || [])
        .map((r) => String(r?.Supplier_AB_Name ?? '').trim()).filter(Boolean))].sort();
    const poNumbers = [...new Map((openCommitments || [])
        .filter((r) => String(r?.PO_Number ?? '').trim())
        .map((r) => [String(r.PO_Number).trim(), String(r?.Supplier_AB_Name ?? '').trim()])).entries()]
        .sort((a, b) => a[0].localeCompare(b[0]));

    return (
        <View style={{ flex: 1, minHeight: 0 }}>
            {offlineBanner}
            {/* One shared datalist feeds every cost-code input in the wizard. */}
            <datalist id="fr-costcodes">
                {codes.map((c) => (
                    <option key={c} value={c}>{descOfCode(c)}</option>
                ))}
            </datalist>
            <datalist id="fr-vendors">
                {poVendors.map((v) => (
                    <option key={v} value={v} />
                ))}
            </datalist>
            <datalist id="fr-pos">
                {poNumbers.map(([po, vendor]) => (
                    <option key={po} value={po}>{vendor}</option>
                ))}
            </datalist>

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, backgroundColor: '#fff', borderBottom: '1px solid #E2E8F0' }}>
                <Pressable onPress={backToList} style={{ minHeight: 32, justifyContent: 'center', paddingRight: 10, cursor: 'pointer' }}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: '#1D4ED8' }}>‹ Reports</Text>
                </Pressable>
                {/* Two columns keep the header two lines tall instead of four:
                    report/step identity on the left, job + report meta on the right. */}
                <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1, minWidth: 0 }}>
                    <View style={{ alignItems: 'flex-end', flexShrink: 0 }}>
                        {/* Constant header title; the step's full name sits beneath it. */}
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A', lineHeight: '17px' }}>{simple ? 'Quick Report' : 'Daily Report'}</Text>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8', lineHeight: '15px' }}>
                            {effectiveView === 'sheet' ? 'Sheet View' : stepDef.title}
                        </Text>
                    </View>
                    <View style={{ width: 1, alignSelf: 'stretch', backgroundColor: '#E2E8F0', marginLeft: 10, marginRight: 10 }} />
                    <View style={{ alignItems: 'flex-start', flexShrink: 1, minWidth: 0 }}>
                        {/* Job identity doubles as the job/team switcher — switching
                            jobs kicks back to that job's report list. */}
                        <JobSwitcher
                            variant="line"
                            textStyle={{ textAlign: 'left', fontSize: 12, lineHeight: '17px' }}
                            onBeforeSwitch={() => { if (!readOnly && dirty && !saving) doSave(); }}
                        />
                        {/* Report meta doubles as the older-reports picker. */}
                        <Pressable onPress={() => { refreshList(); setShowReportPicker(true); }} style={{ cursor: 'pointer', maxWidth: '100%' }}>
                            <Text numberOfLines={1} style={{ fontSize: 11, color: '#64748B', lineHeight: '15px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {[
                                    report.reportDate || '',
                                    report.preparedBy || null,
                                    report.reportNumber ? `#${report.reportNumber}` : null,
                                    readOnly ? 'Submitted (read-only)' : null,
                                ].filter(Boolean).join(' · ')}{' ▾'}
                            </Text>
                        </Pressable>
                    </View>
                </View>
                {/* Guided / Sheet view toggle — the Quick Report is guided-only. */}
                {!simple && (
                    <View style={{ flexDirection: 'row', border: '1px solid #CBD5E1', borderRadius: 8, overflow: 'hidden' }}>
                        {[['wizard', 'Guided'], ['sheet', 'Sheet']].map(([mode, label]) => (
                            <Pressable
                                key={mode}
                                onPress={() => switchView(mode)}
                                style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, backgroundColor: viewMode === mode ? '#1D4ED8' : '#fff', cursor: 'pointer' }}
                            >
                                <Text style={{ fontSize: 12, fontWeight: '700', color: viewMode === mode ? '#fff' : '#475569' }}>{label}</Text>
                            </Pressable>
                        ))}
                    </View>
                )}
            </View>

            <ScrollView style={{ flex: 1 }}>
                <View style={{ width: '100%', maxWidth: effectiveView === 'sheet' || (!simple && stepDef.key === 'review') ? 1240 : 760, marginLeft: 'auto', marginRight: 'auto', padding: 14 }}>
                    {effectiveView === 'sheet' ? (
                        <SheetView readOnly={readOnly} onSaveDraft={onSaveDraft} onSubmit={onSubmit} saving={saving} />
                    ) : (
                        <>
                            {/* Full report only — the Quick Report renders the
                                same block embedded in its review page. */}
                            {stepDef.key === 'setup' && <SetupStep readOnly={readOnly} />}
                            {/* Quick Report Progress tab: the day's schedule
                                (Gantt over the report date) with the chip phase
                                picker, then a combined card per phase — note,
                                people & hours, quantity (HeavyJob-style). */}
                            {simple && stepDef.key === 'progress' ? (
                                <>
                                    <WorkPhaseChips readOnly={readOnly} />
                                    <TimeCard readOnly={readOnly} />
                                </>
                            ) : simple && stepDef.key === 'equipment' ? (
                                /* Quick Report Equipment tab: equipment used and
                                   the subs on site, then the filing block that
                                   used to be its own Review tab (conditions,
                                   safety, issues, recap, photos, notes, submit). */
                                <>
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Equipment Used</Text>
                                    <EquipmentMatrix readOnly={readOnly} />
                                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 14, marginBottom: 6 }}>Subcontractors On Site</Text>
                                    <SubCards readOnly={readOnly} />
                                    <View style={{ height: 18 }} />
                                    <SimpleReview readOnly={readOnly} onSaveDraft={onSaveDraft} onSubmit={onSubmit} saving={saving} />
                                </>
                            ) : stepDef.key === 'quantities' && isPipelineJob(jobData, jobWorkTypes(jobData, jobCostDetail, phaseCodes)) ? (
                                /* Pipeline jobs get the tracker-module quantity
                                   cards; everything else keeps the generic grid. */
                                <QuantityCards readOnly={readOnly} />
                            ) : jsonSectionKeys.concat(['labor', 'quantities']).includes(stepDef.key) && (
                                <SectionRowsStep sectionKey={stepDef.key} readOnly={readOnly} />
                            )}
                            {!simple && stepDef.key === 'review' && (
                                <SheetView readOnly={readOnly} onSaveDraft={onSaveDraft} onSubmit={onSubmit} saving={saving} />
                            )}
                        </>
                    )}
                </View>
            </ScrollView>

            {effectiveView !== 'sheet' && <WizardNav step={Math.min(step, steps.length - 1)} onStep={onStep} saving={saving} dirty={dirty} steps={steps} />}

            {/* Older-reports picker — jump straight to another report for this job. */}
            <Modal
                visible={showReportPicker}
                onRequestClose={() => setShowReportPicker(false)}
                style={{ width: 480, maxWidth: '94vw', maxHeight: '80vh', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderBottom: '1px solid #E2E8F0' }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#0F172A' }}>{`Reports — ${report.buId}`}</Text>
                    <Pressable onPress={() => setShowReportPicker(false)} style={{ minHeight: 32, minWidth: 32, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <Text style={{ fontSize: 16, color: '#64748B' }}>✕</Text>
                    </Pressable>
                </View>
                <ScrollView style={{ maxHeight: '64vh' }}>
                    <View style={{ padding: 12 }}>
                        {loadingList && !reports && (
                            <View style={{ alignItems: 'center', padding: 16 }}>
                                <ActivityIndicator />
                            </View>
                        )}
                        {(reports || []).map((r) => {
                            const current = r.ReportID === report.reportId;
                            return (
                                <Pressable
                                    key={r.ReportID}
                                    onPress={() => {
                                        setShowReportPicker(false);
                                        if (current) return;
                                        if (!readOnly && dirty && !saving) doSave();
                                        openExisting(r);
                                    }}
                                    style={{
                                        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                                        minHeight: 48, borderRadius: 8, paddingLeft: 10, paddingRight: 10, marginBottom: 4, cursor: 'pointer',
                                        backgroundColor: current ? '#EFF6FF' : 'transparent',
                                        border: current ? '1px solid #BFDBFE' : '1px solid #F1F5F9',
                                    }}
                                >
                                    <View style={{ flexShrink: 1, minWidth: 0 }}>
                                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0F172A' }}>
                                            {`#${r.ReportNumber} — ${String(r.ReportDate).slice(0, 10)}`}
                                        </Text>
                                        <Text numberOfLines={1} style={{ fontSize: 11, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {[r.PreparedBy ? `By ${r.PreparedBy}` : null, current ? 'Currently open' : null].filter(Boolean).join(' · ')}
                                        </Text>
                                    </View>
                                    <View style={{ paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: 10, borderRadius: 999, backgroundColor: r.Status === 'Submitted' ? '#ECFDF5' : '#E0F2FE', flexShrink: 0, marginLeft: 8 }}>
                                        <Text style={{ fontSize: 11, fontWeight: '700', color: r.Status === 'Submitted' ? '#065F46' : '#075985' }}>
                                            {openingId === r.ReportID ? 'Opening…' : r.Status}
                                        </Text>
                                    </View>
                                </Pressable>
                            );
                        })}
                        {reports && reports.length === 0 && !loadingList && (
                            <Text style={{ fontSize: 13, color: '#64748B', padding: 8 }}>No other reports for this job.</Text>
                        )}
                    </View>
                </ScrollView>
            </Modal>
        </View>
    );
};

export default DailyFieldReport;
